// components/routes/AppContentWithAuth.js
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Home from "pages/Home";
import Header from "components/layout/Header";
import Banner from "components/layout/Banner";
import Footer from "components/layout/Footer";
import Layout from "components/layout/Layout";
import Page from "pages/Page";
import EventPage from "pages/EventPage";
import Blog from "pages/BlogPage";
import ContactPage from "pages/ContactPage";
import CookiesPage from "pages/CookiesPage";
import MentionsLegalesPage from "pages/MentionsLegalesPage";
import DynamicSubSiteRoute from "components/routes/DynamicSubSiteRoute";
import ProfileSidebar from "components/settings/ProfileSidebar";
import AccessibilitySidebar from "components/settings/AccessibilitySidebar";
import Modal from "components/common/Modal";
import Sidebar from "components/navigation/Sidebar";
import ScrollToTop from "components/routes/ScrollToTop";
import { SubSiteProvider } from "contexts/SubSiteContext";
import PrivateRoute from "components/routes/PrivateRoute";
import ProgrammePage from "pages/ProgrammePage";
import SpectaclesPage from "pages/SpectaclesPage";
import ConcertsPage from "pages/ConcertsPage";
import SpectaclePage from "pages/SpectaclePage";
import LoginPage from "pages/LoginPage";
import MonComptePage from "pages/MonComptePage";
import Spinner from "components/common/Spinner";
import ComingSoonPage from "pages/ComingSoonPage";
import redirectionRoutes from "components/routes/Redirections";
import PanierPage from "pages/Panier";
import OrderFinalization from "pages/OrderFinalization";
import RegisterPage from "pages/RegisterPage";
import AccountConfirmation from "pages/AccountConfirmation";
import Page404 from "pages/Page404";
import Forbidden from "pages/Forbidden";
import CommandConfiramtion from "pages/CommandConfiramtion";
import ForgetPassword from "pages/ForgetPassword";
import ResetPassword from "pages/ResetPassword";
import UnauthRoute from "components/routes/UnauthRoute";
import ConditionsGenerales from "pages/ConditionsGenerales";
import Stream from "pages/Stream";
import { useAuth } from "contexts/AuthContext";
import api from "services/api";

function AppContentWithAuth() {
  const { authChecked } = useAuth();
  const isRedirectionEnabled = process.env.REACT_APP_ENABLE_REDIRECTION === "true";
  
  const [isLanding, setIsLanding] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // N'effectuer la requête de configuration que si l'auth est vérifiée
    if (authChecked) {
      api
        .get("/settings/landing")
        .then((res) => {
          setIsLanding(res.data.isLanding);
        })
        .catch((error) => {
          console.error("Erreur lors de la récupération /settings/landing :", error);
          setIsLanding(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [authChecked]);

  // Attendre que l'auth soit vérifiée ET que la config soit chargée
  if (!authChecked || loading) {
    return <Spinner />;
  }

  return (
    <>
      {isLanding ? (
        // ----- Mode Landing page seulement -----
        <Routes>
          <Route path="/" element={<ComingSoonPage />} />
          {/* On redirige toute route vers la landing */}
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      ) : (
        // ----- Mode "normal" de l'application -----
        <SubSiteProvider>
          <Layout>
            <Header />
            <Banner />
            <ScrollToTop />
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/actualite" element={<Blog />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/programme" element={<ProgrammePage />} />
              <Route path="/programme/spectacle/:spectacleSlug" element={<SpectaclePage />} />
              <Route path="/programme/concert/:spectacleSlug" element={<SpectaclePage />} />
              <Route path="/programme/stream" element={<Stream />} />
              <Route path="/programme/stream/:streamCode" element={<Stream />} />
              <Route path="/programme/spectacle" element={<SpectaclesPage />} />
              <Route path="/programme/concert" element={<ConcertsPage />} />
              <Route path="/panier" element={<PanierPage />} />
              <Route path="/panier/finaliser-votre-commande" element={<OrderFinalization />} />

              <Route path="/login" element={<LoginPage />} />
              <Route path="/mot-de-passe-oublie" element={
                <UnauthRoute>
                  <ForgetPassword />
                </UnauthRoute>
              } />
              <Route path="/reinitialisation" element={
                <UnauthRoute>
                  <ResetPassword />
                </UnauthRoute>
              } />
              <Route path="/inscription" element={<RegisterPage />} />
              <Route path="/confirmation" element={<AccountConfirmation />} />
              <Route path="/page-introuvable" element={<Page404 />} />
              <Route path="/accès-interdit" element={<Forbidden />} />
              <Route path="/commande-retour" element={<CommandConfiramtion />} />

              {/*Pages protégées*/}
              <Route
                path="/mon-compte"
                element={
                  <PrivateRoute>
                    <MonComptePage />
                  </PrivateRoute>
                }
              />

              <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
              <Route path="/conditions-generales" element={<ConditionsGenerales />} />
              <Route path="/cookies" element={<CookiesPage />} />
              <Route path="/evenement/:eventSlug" element={<EventPage />} />
              <Route path="/page/:categorySlug/:pageSlug" element={<Page />} />
              <Route path="/page/:pageSlug" element={<Page />} />
              <Route path="/:subSiteSlug" element={<DynamicSubSiteRoute />} />
              <Route path="/:subSiteSlug/page/:pageSlug" element={<Page />} />
              <Route path="/:subSiteSlug/evenement/:eventSlug" element={<EventPage />} />
              <Route path="/:subSiteSlug/actualite" element={<Blog />} />

              {isRedirectionEnabled && redirectionRoutes}

              <Route path="*" element={<Page404 />} />
            </Routes>
            <Footer />
          </Layout>
          <ProfileSidebar />
          <AccessibilitySidebar />
          <Sidebar />
        </SubSiteProvider>
      )}
      <Modal />
    </>
  );
}

export default AppContentWithAuth;
